// Third-party
import { useState, useRef, useEffect } from 'react';
import anime from 'animejs';

// First-party
import { For, Slot, createComponent } from '@lib/util/templateHelpers';
import useNotifications from '@lib/hooks/notifications';

// Component lib
import shared from '@lib/components';
const { 
  Flex,
  Button
} = shared;

// Local
import IoGameTile from '@/components/IoGameTile';
import GameTileBlock from '@/components/GameTileBlock';
import { trackComscorePageview } from '../../util/trackComscorePageview';

export default createComponent('AllGamesBlock', {}, function AllGamesBlock ({ className, slots }, props) {
  const { notify } = useNotifications();
  const [ moreGames, setMoreGames ] = useState([]);
  const [ loadingMoreGames, setLoadingMoreGames ] = useState(false);

  const offsetRef = useRef(props.games.length);
  const gameIds = useRef(props.games.map((game) => game.nid));

  const loadMoreGames = async () => {
    trackComscorePageview();

    const limit = process.env.APP_CONFIG.ALL_GAMES_BLOCK_LOAD_MORE_LIMIT;

    setLoadingMoreGames(true);
    const nextMoreGames = await props.fetchMoreGames(limit, offsetRef.current);
    setLoadingMoreGames(false);

    if (nextMoreGames.length === 0) {
      notify('You\'ve reached the end of the list.');
      return;
    }

    setMoreGames((currentGames) => {
      const found = [ ...gameIds.current ];
      const nextGames = currentGames
        .concat(nextMoreGames)
        .filter(game => {
          const result = found.includes(game.nid);
          found.push(game.nid);
          return !result; 
        });
      offsetRef.current += nextGames.length;
      return nextGames;
    });
  };

  useEffect(() => {
    (async () => {
      const hiddenGameTiles = Array.from(document.querySelectorAll('.IoGameTile--transitionIn'));
      hiddenGameTiles.forEach((el) => el.classList.remove('IoGameTile--transitionIn'));

      await anime({
        targets: hiddenGameTiles,
        opacity: {
          easing: 'linear',
          value: [0, 1],
          duration: 200
        },
        scale: [0.5, 1],
        delay: anime.stagger(30)
      }).finished;

      hiddenGameTiles.forEach((el) => el.classList.remove('IoGameTile--hidden'));
    })();
  }, [moreGames]);

  return (
    <GameTileBlock center={props.center} className={className}>
      <Slot name='title'><h1>{slots.title}</h1></Slot>
      <Slot name='description'>{slots.description}</Slot>
      {props.games.map((game, index) => (
        <IoGameTile 
          key={`${index}__${game.id}__preload`} 
          game={game} 
          featured={props.feature && index === 0} 
        />
      ))}
      {moreGames.map((game, index) => (
        <IoGameTile 
          key={`${index}__${game.id}`} 
          game={game} 
          className='IoGameTile--hidden IoGameTile--transitionIn' 
        />
      ))}
      <Slot name='control'>
        <Flex>
          <Flex center>
            <Button primary loading={loadingMoreGames} disabled={loadingMoreGames} onClick={loadMoreGames}>More Games</Button>
          </Flex>
        </Flex>
      </Slot>
    </GameTileBlock>
  );
});